import type { Coredata } from 'webapp-types';

// Snapshot of window.coredata.cdrRanges / cdrGranulations from prod, used as
// seed defaults so the e2e seed can boot DateRange without a real coredata
// payload. Refresh from view-source of /app or via `seedGlobals({ cdrRanges, ... })`.

// the snapshot also has legacyIntervals and tags, which the app doesn't read
type CdrRanges = Record<
	string,
	Coredata.Constants['cdrRanges'][string] & { legacyIntervals?: unknown; tags?: number[] }
>;

export const DEFAULT_CDR_RANGES = {
	AllTime: {
		formula: {
			timeUnit: null,
			multiplier: null,
			offset: null,
			includeFuture: true,
			isAllTime: true,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: { 99: [] },
	},
	Today: {
		formula: {
			timeUnit: 2,
			multiplier: 1,
			offset: 0,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: { 1: [] },
	},
	ThisWeek: {
		formula: {
			timeUnit: 3,
			multiplier: 1,
			offset: 0,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: { 25: [] },
	},
	ThisMonth: {
		formula: {
			timeUnit: 4,
			multiplier: 1,
			offset: 0,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: { 15: [] },
	},
	ThisQuarter: {
		formula: {
			timeUnit: 5,
			multiplier: 1,
			offset: 0,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: { 26: [] },
	},
	ThisYear: {
		formula: {
			timeUnit: 6,
			multiplier: 1,
			offset: 0,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: { 27: [] },
	},
	TodayToHour: {
		formula: {
			timeUnit: 2,
			multiplier: 1,
			offset: 0,
			includeFuture: false,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: [],
	},
	WeekToDate: {
		formula: {
			timeUnit: 3,
			multiplier: 1,
			offset: 0,
			includeFuture: false,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: { 3: [] },
	},
	MonthToDate: {
		formula: {
			timeUnit: 4,
			multiplier: 1,
			offset: 0,
			includeFuture: false,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: { 5: [] },
	},
	QuarterToDate: {
		formula: {
			timeUnit: 5,
			multiplier: 1,
			offset: 0,
			includeFuture: false,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: { 7: [] },
	},
	YearToDate: {
		formula: {
			timeUnit: 6,
			multiplier: 1,
			offset: 0,
			includeFuture: false,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: { 8: [] },
	},
	Yesterday: {
		formula: {
			timeUnit: 2,
			multiplier: 1,
			offset: -1,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: { 2: [] },
	},
	PreviousWeek: {
		formula: {
			timeUnit: 3,
			multiplier: 1,
			offset: -1,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: { 12: [] },
	},
	PreviousMonth: {
		formula: {
			timeUnit: 4,
			multiplier: 1,
			offset: -1,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: { 13: [] },
	},
	PreviousQuarter: {
		formula: {
			timeUnit: 5,
			multiplier: 1,
			offset: -1,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: { 23: [] },
	},
	PreviousYear: {
		formula: {
			timeUnit: 6,
			multiplier: 1,
			offset: -1,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: { 24: [] },
	},
	LastXHours: {
		formula: {
			timeUnit: 1,
			multiplier: 'X > 1',
			offset: 0,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: [],
	},
	LastXHoursWithoutCurrent: {
		formula: {
			timeUnit: 1,
			multiplier: 'X > 1',
			offset: -1,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: { 11: { multiplier: 24 } },
	},
	LastXDays: {
		formula: {
			timeUnit: 2,
			multiplier: 'X > 1',
			offset: 0,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: [],
	},
	LastXDaysWithoutCurrent: {
		formula: {
			timeUnit: 2,
			multiplier: 'X > 1',
			offset: -1,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: {
			4: { multiplier: 7 },
			6: { multiplier: 30 },
			10: { multiplier: 14 },
			22: { multiplier: 28 },
			28: { multiplier: 90 },
			29: { multiplier: 180 },
		},
	},
	LastXDaysRolling: {
		formula: {
			timeUnit: 2,
			multiplier: 'X > 0',
			offset: 'X < -1 || X > 0',
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: [],
	},
	LastXWeeks: {
		formula: {
			timeUnit: 3,
			multiplier: 'X > 1',
			offset: 0,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: [],
	},
	LastXWeeksWithoutCurrent: {
		formula: {
			timeUnit: 3,
			multiplier: 'X > 1',
			offset: -1,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: [],
	},
	LastXMonths: {
		formula: {
			timeUnit: 4,
			multiplier: 'X > 1',
			offset: 0,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: [],
	},
	LastXMonthsWithoutCurrent: {
		formula: {
			timeUnit: 4,
			multiplier: 'X > 1',
			offset: -1,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: { 9: { multiplier: 12 } },
	},
	LastXQuarters: {
		formula: {
			timeUnit: 5,
			multiplier: 'X > 1',
			offset: 0,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: [],
	},
	LastXQuartersWithoutCurrent: {
		formula: {
			timeUnit: 5,
			multiplier: 'X > 1',
			offset: -1,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: [],
	},
	LastXYears: {
		formula: {
			timeUnit: 6,
			multiplier: 'X > 1',
			offset: 0,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: [],
	},
	LastXYearsWithoutCurrent: {
		formula: {
			timeUnit: 6,
			multiplier: 'X > 1',
			offset: -1,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: [],
	},
	SinceToDate: {
		formula: {
			timeUnit: 2,
			multiplier: null,
			offset: 0,
			includeFuture: true,
			isAllTime: false,
			startDate: 'ISO-8610 YYYY-MM-DD',
			endDate: null,
		},
		legacyIntervals: [],
	},
	SinceToDateWithoutCurrent: {
		formula: {
			timeUnit: 2,
			multiplier: null,
			offset: -1,
			includeFuture: true,
			isAllTime: false,
			startDate: 'ISO-8610 YYYY-MM-DD',
			endDate: null,
		},
		legacyIntervals: [],
	},
	Tomorrow: {
		formula: {
			timeUnit: 2,
			multiplier: 1,
			offset: 1,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: [],
		tags: [1],
	},
	NextWeek: {
		formula: {
			timeUnit: 3,
			multiplier: 1,
			offset: 1,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: [],
		tags: [1],
	},
	NextMonth: {
		formula: {
			timeUnit: 4,
			multiplier: 1,
			offset: 1,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: [],
		tags: [1],
	},
	NextQuarter: {
		formula: {
			timeUnit: 5,
			multiplier: 1,
			offset: 1,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: [],
		tags: [1],
	},
	NextYear: {
		formula: {
			timeUnit: 6,
			multiplier: 1,
			offset: 1,
			includeFuture: true,
			isAllTime: false,
			startDate: null,
			endDate: null,
		},
		legacyIntervals: [],
		tags: [1],
	},
	CustomRange: {
		formula: {
			timeUnit: null,
			multiplier: null,
			offset: null,
			includeFuture: true,
			isAllTime: false,
			startDate: 'ISO-8610 YYYY-MM-DD',
			endDate: 'ISO-8610 YYYY-MM-DD',
		},
		legacyIntervals: [],
	},
} satisfies CdrRanges;

export const DEFAULT_CDR_GRANULATIONS = {
	Total: { formula: { timeUnit: null, multiplier: null, isWholeRange: true } },
	Hourly: { formula: { timeUnit: 1, multiplier: 1, isWholeRange: false } },
	'2Hours': { formula: { timeUnit: 1, multiplier: 2, isWholeRange: false } },
	'6Hours': { formula: { timeUnit: 1, multiplier: 6, isWholeRange: false } },
	'12Hours': { formula: { timeUnit: 1, multiplier: 12, isWholeRange: false } },
	Daily: { formula: { timeUnit: 2, multiplier: 1, isWholeRange: false } },
	Weekly: { formula: { timeUnit: 3, multiplier: 1, isWholeRange: false } },
	Monthly: { formula: { timeUnit: 4, multiplier: 1, isWholeRange: false } },
	Quarterly: { formula: { timeUnit: 5, multiplier: 1, isWholeRange: false } },
	Yearly: { formula: { timeUnit: 6, multiplier: 1, isWholeRange: false } },
	None: { formula: { timeUnit: null, multiplier: null, isWholeRange: false } },
} satisfies Coredata.Constants['cdrGranulations'];
